import type { CanaryRecommendation } from '@/api/aiVisibility';
import { InstanceLink } from '@/components/routing';
import { Text, Title } from '@/components/typography';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/router';

import RecommendationTags from './RecommendationTags';

type RecommendationCardProps = {
  recommendation: CanaryRecommendation;
  truncateDiagnosis?: boolean;
  onClick?: () => void;
};

const RecommendationCard = ({
  recommendation,
  truncateDiagnosis = false,
  onClick,
}: RecommendationCardProps) => {
  const Card = onClick ? 'button' : 'article';

  return (
    <Card
      type={onClick ? 'button' : undefined}
      className={cn(
        'flex w-full max-w-(--breakpoint-lg) flex-col items-start gap-2 rounded-2xl border-3 border-gray-300 bg-white px-5 py-4 text-left',
        onClick &&
          'cursor-pointer transition-colors hover:border-gray-400 focus-visible:border-green-500 focus-visible:outline-none',
      )}
      onClick={onClick}
    >
      {onClick ? (
        <Text variant="label">{recommendation.title}</Text>
      ) : (
        <Title level={3} variant="label">
          <InstanceLink
            className="underline underline-offset-2 focus-visible:outline-green-500"
            to={{
              pathname: ROUTES.aiVisibilityRecommendations.path,
              search: `?${new URLSearchParams({ recommendationId: recommendation.id })}`,
            }}
          >
            {recommendation.title}
          </InstanceLink>
        </Title>
      )}
      {recommendation.diagnosis && (
        <Text
          variant={truncateDiagnosis ? 'body2' : 'label'}
          weight="regular"
          className={cn(
            'w-full wrap-break-word whitespace-pre-wrap',
            truncateDiagnosis && 'line-clamp-4',
          )}
        >
          {recommendation.diagnosis}
        </Text>
      )}
      <RecommendationTags
        recommendation={recommendation}
        className={truncateDiagnosis ? 'xl:mt-auto' : undefined}
      />
    </Card>
  );
};

export default RecommendationCard;
